(function () {
  'use strict';

  function uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  function getOrCreateSessionId() {
    let id = localStorage.getItem('rv_session_id');
    if (!id) { id = uuid(); localStorage.setItem('rv_session_id', id); }
    return id;
  }

  class RoomVisualizerModal {
    constructor(rootEl) {
      this.root = rootEl;
      this.endpoint = rootEl.dataset.endpoint;
      this.productId = rootEl.dataset.productId;
      this.productTitle = rootEl.dataset.productTitle;
      this.productHandle = rootEl.dataset.productHandle;
      this.category = rootEl.dataset.productCategory || 'plant';
      this.locale = rootEl.dataset.locale;
      this.sessionId = getOrCreateSessionId();
      this.state = {
        currentStep: 1,
        photoBlob: null,
        photoDataUrl: null,
        roomContext: null,
        placementX: null,
        placementY: null,
        replacementObject: null,
        imageId: null,
        imageUrl: null,
      };
      this._bind();
    }

    _bind() {
      this.root.querySelectorAll('[data-rv-close]').forEach(el => {
        el.addEventListener('click', () => this.close());
      });
      document.addEventListener('keydown', (e) => {
        if (!this.root.hasAttribute('hidden') && e.key === 'Escape') this.close();
      });
      this._bindStep1();
      this._bindStep2();
    }

    _bindStep1() {
      this.root.querySelectorAll('[data-rv-photo-input]').forEach(input => {
        input.addEventListener('change', (e) => this._onPhotoSelected(e.target.files[0], input.closest('[data-rv-source]').dataset.rvSource));
      });
      this.root.querySelectorAll('[data-rv-chip]').forEach(chip => {
        chip.addEventListener('click', () => this._onChipSelected(chip));
      });
      this.root.querySelector('[data-rv-photo-change]')?.addEventListener('click', () => this._onPhotoChange());
      this.root.querySelector('[data-rv-next]')?.addEventListener('click', () => this._onNext());
      this.root.querySelector('[data-rv-back]')?.addEventListener('click', () => this._onBack());
    }

    _showPhotoPreview(dataUrl) {
      const grid = this.root.querySelector('[data-rv-upload-grid]');
      const tip = this.root.querySelector('[data-rv-photo-tip]');
      const preview = this.root.querySelector('[data-rv-photo-preview]');
      const previewImg = this.root.querySelector('[data-rv-photo-preview-img]');
      if (grid) grid.hidden = true;
      if (tip) tip.hidden = true;
      if (previewImg && dataUrl) previewImg.src = dataUrl;
      if (preview) preview.hidden = false;
    }

    _hidePhotoPreview() {
      const grid = this.root.querySelector('[data-rv-upload-grid]');
      const tip = this.root.querySelector('[data-rv-photo-tip]');
      const preview = this.root.querySelector('[data-rv-photo-preview]');
      const previewImg = this.root.querySelector('[data-rv-photo-preview-img]');
      if (preview) preview.hidden = true;
      if (previewImg) previewImg.removeAttribute('src');
      if (grid) grid.hidden = false;
      if (tip) tip.hidden = false;
    }

    _onPhotoChange() {
      this.state.photoBlob = null;
      this.state.photoDataUrl = null;
      this.root.querySelectorAll('[data-rv-photo-input]').forEach(i => { i.value = ''; });
      this._hidePhotoPreview();
      this._disableNext();
    }

    _bindStep2() {
      const canvas = this.root.querySelector('[data-rv-canvas]');
      if (!canvas) return;
      canvas.addEventListener('click', (e) => this._onCanvasClick(e, canvas));

      const replaceInput = this.root.querySelector('[data-rv-replace-input]');
      if (replaceInput) {
        replaceInput.addEventListener('input', (e) => {
          this.state.replacementObject = e.target.value.trim() || null;
        });
      }
    }

    _onCanvasClick(e, canvasEl) {
      const rect = canvasEl.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      this.state.placementX = x;
      this.state.placementY = y;
      const marker = this.root.querySelector('[data-rv-marker]');
      if (marker) {
        marker.style.left = `${x * 100}%`;
        marker.style.top = `${y * 100}%`;
        marker.hidden = false;
      }
      this._track('visualizer_placement_set', {
        placement_x: x, placement_y: y, has_replacement: !!this.state.replacementObject,
      });
      const next = this.root.querySelector('[data-rv-next]');
      if (next) next.disabled = false;
    }

    async _onPhotoSelected(file, source) {
      if (!file) return;

      // Always normalise via the server's /api/preview endpoint. Sharp (libvips
      // + libheif) handles HEIC, HEIF, JPEG, PNG, WebP and corrects EXIF
      // orientation. Returns a JPEG data URL we can both render in <img> and
      // re-use as the upload payload for /api/visualize.
      this._setUploadStatus(this._t('photo_processing') || 'Foto verwerken…');
      try {
        const fd = new FormData();
        fd.append('photo', file);
        const res = await fetch(`${this.endpoint}/api/preview`, { method: 'POST', body: fd });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || `preview failed (${res.status})`);
        }
        const { data_url } = await res.json();
        const blob = await (await fetch(data_url)).blob();

        this.state.photoBlob = blob;
        this.state.photoDataUrl = data_url;
        this._track('visualizer_photo_uploaded', {
          source,
          original_size_kb: Math.round(file.size / 1024),
          normalized_size_kb: Math.round(blob.size / 1024),
          original_type: file.type || 'unknown',
        });
        this._setUploadStatus(null);
        this._showPhotoPreview(data_url);
        this._enableNext();
      } catch (err) {
        console.error('[rv] photo selection failed:', err);
        this._setUploadStatus(null);
        alert(this._t('photo_load_failed'));
        const inputEl = this.root.querySelector('[data-rv-photo-input]');
        if (inputEl) inputEl.value = '';
      }
    }

    _setUploadStatus(text) {
      let statusEl = this.root.querySelector('[data-rv-upload-status]');
      if (!text) {
        if (statusEl) statusEl.remove();
        return;
      }
      if (!statusEl) {
        statusEl = document.createElement('p');
        statusEl.className = 'rv-tip';
        statusEl.dataset.rvUploadStatus = '';
        const grid = this.root.querySelector('.rv-upload-grid');
        if (grid && grid.parentNode) grid.parentNode.insertBefore(statusEl, grid.nextSibling);
      }
      statusEl.textContent = text;
    }

    _onChipSelected(chipEl) {
      this.root.querySelectorAll('[data-rv-chip]').forEach(c => c.classList.remove('rv-chip--active'));
      chipEl.classList.add('rv-chip--active');
      this.state.roomContext = chipEl.dataset.rvChip;
      this._track('visualizer_room_chip_selected', { chip_value: this.state.roomContext });
    }

    _enableNext() {
      const btn = this.root.querySelector('[data-rv-next]');
      if (btn) btn.disabled = false;
    }

    _disableNext() {
      const btn = this.root.querySelector('[data-rv-next]');
      if (btn) btn.disabled = true;
    }

    _onNext() {
      const next = this.state.currentStep + 1;
      if (next === 2) {
        this._setStep(2);
        const img = this.root.querySelector('[data-rv-canvas-img]');
        if (img) img.src = this.state.photoDataUrl;
        this._disableNext();
        const back = this.root.querySelector('[data-rv-back]');
        if (back) back.hidden = false;
      } else if (next === 3) {
        this._setStep(3);
        this._generate();
      }
    }

    _onBack() {
      if (this.state.currentStep > 1) this._setStep(this.state.currentStep - 1);
    }

    _readAsDataUrl(blob) {
      return new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(blob);
      });
    }

    async _compressImage(file, maxBytes) {
      if (file.size <= maxBytes) return file;
      const img = await this._loadImage(file);
      const scale = Math.sqrt(maxBytes / file.size);
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(img.naturalWidth * scale);
      canvas.height = Math.floor(img.naturalHeight * scale);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.85));
    }

    _loadImage(blob) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = reject;
        img.src = url;
      });
    }

    _resetState() {
      this.state = {
        currentStep: 1,
        photoBlob: null,
        photoDataUrl: null,
        roomContext: null,
        placementX: null,
        placementY: null,
        replacementObject: null,
        imageId: null,
        imageUrl: null,
      };
      this._reviewsPopulated = false;
      // Clear any leftover step-1 UI state across reopens.
      this.root.querySelectorAll('[data-rv-chip]').forEach(c => c.classList.remove('rv-chip--active'));
      const marker = this.root.querySelector('[data-rv-marker]');
      if (marker) marker.hidden = true;
      const replaceInput = this.root.querySelector('[data-rv-replace-input]');
      if (replaceInput) replaceInput.value = '';
      this.root.querySelectorAll('[data-rv-photo-input]').forEach(i => { i.value = ''; });
      this._hidePhotoPreview();
      const next = this.root.querySelector('[data-rv-next]');
      if (next) next.disabled = true;
      const back = this.root.querySelector('[data-rv-back]');
      if (back) back.hidden = true;
    }

    open() {
      this._resetState();
      this.root.removeAttribute('hidden');
      this.root.setAttribute('aria-hidden', 'false');
      document.body.classList.add('rv-modal-open');
      this._setStep(1);
      this._track('visualizer_open', { product_id: this.productId, category: this.category });
    }

    close() {
      if (this.state.currentStep === 3) {
        if (!confirm(this._t('error_msg'))) return;
      }
      this._teardownSlider();
      this._stopProgressAnimation();
      this.root.setAttribute('hidden', '');
      this.root.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('rv-modal-open');
    }

    _setStep(n) {
      this.state.currentStep = n;
      this.root.querySelectorAll('[data-rv-step]').forEach(el => {
        el.hidden = String(el.dataset.rvStep) !== String(n);
      });
      this.root.querySelectorAll('[data-rv-step-pill]').forEach(pill => {
        const i = parseInt(pill.dataset.rvStepPill, 10);
        pill.classList.toggle('rv-modal__step--active', i === n);
        pill.classList.toggle('rv-modal__step--done', i < n);
      });
      const titleEl = this.root.querySelector('[data-rv-title]');
      if (titleEl) {
        const map = { 1: 'step1_title', 2: 'step2_title', 3: 'step3_title', 4: 'step4_title' };
        if (map[n]) titleEl.textContent = this._t(map[n]);
      }
      // The footer's Back/Next belong to the photo→placement→generate flow only.
      // On the result step (4) and the error step there is no "next", so hide the
      // footer — its own CTAs (add to cart, retry) drive the rest.
      const footer = this.root.querySelector('[data-rv-footer]');
      if (footer) footer.hidden = (n === 4 || n === 'error');
    }

    _decodeEntities(s) {
      if (typeof s !== 'string') return s;
      // Shopify's | t filter HTML-escapes output; decode common entities back so
      // alerts/textContent show the real characters (e.g. apostrophe).
      const map = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&#39;': "'", '&quot;': '"', '&apos;': "'" };
      return s.replace(/&(amp|lt|gt|#39|quot|apos);/g, (m) => map[m] || m);
    }

    _t(key) {
      if (!this._i18n) {
        try {
          const el = this.root.querySelector('[data-rv-i18n]');
          const parsed = el ? JSON.parse(el.textContent) : {};
          this._i18n = {};
          for (const [k, v] of Object.entries(parsed)) {
            this._i18n[k] = this._decodeEntities(v);
          }
        } catch (e) {
          this._i18n = {};
        }
      }
      return this._i18n[key] ?? key;
    }

    _populateReviews() {
      const listEl = this.root.querySelector('[data-rv-reviews-list]');
      if (!listEl || this._reviewsPopulated) return;

      this._populateReviewSummary();

      // Prefer live DOM reviews from the PDP's Judge.me widget. The widget is
      // server-rendered via product.metafields.judgeme.widget, so cards exist
      // on page load. Falls back only to Judge.me's JSON island.
      const liveCards = this._extractLiveJudgemeReviews();
      if (liveCards.length > 0) {
        this._renderReviewCards(listEl, liveCards);
        this._reviewsPopulated = true;
        return;
      }

      const dataEl = this.root.querySelector('[data-rv-jdgm-data]');
      if (!dataEl) return;
      this._reviewsPopulated = true;

      let raw;
      try { raw = JSON.parse(dataEl.textContent); } catch (e) { return; }

      const reviews = (
        raw.reviews
        || raw.review_widget?.reviews
        || raw.data?.reviews
        || []
      );

      const filtered = reviews
        .filter(r => Number(r.rating) >= 4)
        .slice(0, 5)
        .map(r => ({
          rating: Number(r.rating) || 5,
          body: (r.body || r.review_body || r.content || '').toString().trim(),
          author: r.reviewer?.name || r.reviewer_name || r.name || 'Anoniem',
        }));

      if (filtered.length === 0) return;
      this._renderReviewCards(listEl, filtered);
    }

    _populateReviewSummary() {
      const avgEl = this.root.querySelector('[data-rv-reviews-avg]');
      const countEl = this.root.querySelector('[data-rv-reviews-count]');
      const sepEl = this.root.querySelector('[data-rv-reviews-sep]');
      if (!avgEl && !countEl) return;

      const avgText = (document.querySelector('.js-jdgm-avg')?.textContent || '').trim();
      const countText = (document.querySelector('.js-jdgm-count')?.textContent || '').trim();
      const validAvg = avgText && avgText !== '—';
      const validCount = countText && !countText.startsWith('—');

      if (avgEl) {
        if (validAvg) { avgEl.textContent = avgText; avgEl.hidden = false; }
        else avgEl.hidden = true;
      }
      if (countEl) {
        if (validCount) { countEl.textContent = `· ${countText}`; countEl.hidden = false; }
        else countEl.hidden = true;
      }
      if (sepEl) sepEl.hidden = !(validAvg || validCount);
    }

    _extractLiveJudgemeReviews() {
      const widget = document.getElementById('judgeme_product_reviews');
      if (!widget) return [];
      const cards = widget.querySelectorAll('.jdgm-rev');
      const results = [];
      cards.forEach(card => {
        const ratingAttr = card.querySelector('.jdgm-rev__rating')?.getAttribute('data-score')
          || card.getAttribute('data-rating')
          || card.getAttribute('data-score');
        let rating = Number(ratingAttr);
        if (!rating) {
          rating = card.querySelectorAll('.jdgm-star.jdgm--on, .jdgm-star--on').length || 5;
        }
        if (rating < 4) return;
        const body = (card.querySelector('.jdgm-rev__body')?.textContent
          || card.querySelector('.jdgm-rev__content')?.textContent
          || '').trim();
        if (!body) return;
        const author = (card.querySelector('.jdgm-rev__author')?.textContent
          || card.querySelector('.jdgm-rev__reviewer-name')?.textContent
          || 'Anoniem').trim();
        results.push({ rating, body, author });
      });
      return results.slice(0, 5);
    }

    _renderReviewCards(listEl, items) {
      listEl.innerHTML = '';
      for (const r of items) {
        const card = document.createElement('article');
        card.className = 'rv-review';
        const stars = '★'.repeat(Math.min(5, Math.max(0, Math.round(r.rating))));
        const truncated = r.body.length > 110 ? r.body.slice(0, 110).trim() + '…' : r.body;
        card.innerHTML = `
          <div class="rv-review__stars">${stars}</div>
          <p class="rv-review__quote">"${this._escapeHtml(truncated)}"</p>
          <span class="rv-review__author">— ${this._escapeHtml(r.author)}</span>
        `;
        listEl.appendChild(card);
      }
      const reviews = listEl.closest('[data-rv-reviews]');
      if (reviews) reviews.hidden = items.length === 0;
    }

    _escapeHtml(s) {
      const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
      return String(s).replace(/[&<>"']/g, ch => map[ch]);
    }

    _track(eventName, props) {
      if (window.dataLayer) window.dataLayer.push({ event: eventName, ...props });
      if (window.analytics?.track) window.analytics.track(eventName, props);
    }

    async _generate() {
      const startTime = Date.now();
      this._populateReviews();
      this._renderOptimistic();
      this._startProgressAnimation();

      const formData = new FormData();
      formData.append('room_photo', this.state.photoBlob, 'room.jpg');
      formData.append('product_id', this.productId);
      formData.append('variant_id', this._currentVariantId());
      const potProductId = this._selectedPotProductId();
      const potVariantId = this._selectedPotVariantId();
      if (potProductId) {
        formData.append('pot_product_id', potProductId);
        console.log(`[rv] generating with pre-selected pot product=${potProductId} variant=${potVariantId}`);
      }
      formData.append('placement_x', String(this.state.placementX));
      formData.append('placement_y', String(this.state.placementY));
      if (this.state.replacementObject) formData.append('replacement_object', this.state.replacementObject);
      if (this.state.roomContext) formData.append('room_context', this.state.roomContext);
      formData.append('product_category', this.category);
      formData.append('session_id', this.sessionId);

      try {
        const res = await fetch(`${this.endpoint}/api/visualize`, { method: 'POST', body: formData });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          if (err.reason === 'unavailable') {
            // AI quota / billing credits exhausted server-side — retrying won't
            // help until topped up, so show a "temporarily unavailable" message.
            this._showError(this._t('unavailable'));
          } else if (res.status === 429) {
            const reasonKeyMap = {
              session_product_limit: 'rate_limit_product',
              session_hourly_limit: 'rate_limit_hour',
              session_daily_limit: 'rate_limit_day',
              ip_hourly_limit: 'rate_limit_network',
            };
            const key = reasonKeyMap[err.reason] || 'rate_limit';
            const msg = this._t(key);
            this._showError(msg === key ? this._t('rate_limit') : msg);
          } else {
            this._showError(this._t('error_msg'));
          }
          this._track('visualizer_failure', { reason: err.reason || res.status, attempt_count: 1 });
          return;
        }
        const data = await res.json();
        this.state.imageId = data.image_id;
        this.state.imageUrl = data.image_url;
        this._stopProgressAnimation();
        this._track('visualizer_generated', { duration_ms: Date.now() - startTime, success: true, has_preselected_pot: !!potProductId });
        this._showResult();
      } catch (err) {
        console.error('[rv] generate error:', err);
        this._showError(this._t('error_msg'));
        this._track('visualizer_failure', { reason: 'network_error', attempt_count: 1 });
      }
    }

    _currentVariantId() {
      // Prefer the unique main-PDP hidden input — base-minicart renders other
      // [name="id"] inputs for related products which would otherwise win.
      const main = document.getElementById('variant-id');
      if (main?.value) return main.value;
      const sel = document.querySelector('select[name="id"]');
      if (sel?.value) return sel.value;
      const checked = document.querySelector('input[name="id"]:checked');
      if (checked?.value) return checked.value;
      return '';
    }

    _selectedPotProductId() {
      const checked = document.querySelector('input[name="addExtraPot"]:checked');
      return checked?.dataset?.productId || null;
    }

    _renderOptimistic() {
      const genImg = this.root.querySelector('[data-rv-gen-img]');
      const overlay = this.root.querySelector('[data-rv-gen-product-overlay]');
      if (genImg) genImg.src = this.state.photoDataUrl;
      if (overlay) {
        const productImg = document.querySelector('.product-hero-main__image img, .product__image img, .product-single__image img, .product-image img');
        if (productImg?.src) {
          overlay.src = productImg.src;
          overlay.style.left = `${this.state.placementX * 100}%`;
          overlay.style.bottom = `${(1 - this.state.placementY) * 100}%`;
        }
      }
    }

    _startProgressAnimation() {
      const phaseEl = this.root.querySelector('[data-rv-phase]');
      const fillEl = this.root.querySelector('[data-rv-progress-fill]');
      const timeEl = this.root.querySelector('[data-rv-time-left]');
      const phases = [
        { key: 'phase_analyze', untilMs: 7000, pct: 18 },
        { key: 'phase_scale', untilMs: 15000, pct: 42 },
        { key: 'phase_integrate', untilMs: 25000, pct: 70 },
        { key: 'phase_lighting', untilMs: 35000, pct: 92 },
        { key: 'phase_almost', untilMs: 90000, pct: 98 },
      ];
      const start = Date.now();
      this._progressTimer = setInterval(() => {
        const elapsed = Date.now() - start;
        const phase = phases.find(p => elapsed < p.untilMs) || phases[phases.length - 1];
        if (phaseEl) phaseEl.textContent = this._t(phase.key);
        if (fillEl) fillEl.style.width = `${phase.pct}%`;
        const remaining = Math.max(0, Math.round((35000 - elapsed) / 1000));
        if (timeEl) timeEl.textContent = remaining > 0 ? `~${remaining}s` : '~klaar';
      }, 500);
    }

    _stopProgressAnimation() {
      if (this._progressTimer) { clearInterval(this._progressTimer); this._progressTimer = null; }
    }

    _showResult() {
      this._setStep(4);
      const before = this.root.querySelector('[data-rv-result-before]');
      const after = this.root.querySelector('[data-rv-result-after]');
      if (before) before.src = this.state.photoDataUrl;
      if (after) after.src = this.state.imageUrl;
      this._initResultSlider();
      this._populateCtaBlock();
    }

    _showError(msg) {
      this._stopProgressAnimation();
      this._setStep('error');
      const errorEl = this.root.querySelector('.rv-error-msg');
      if (errorEl) errorEl.textContent = msg;
      const retry = this.root.querySelector('[data-rv-retry]');
      if (retry) retry.onclick = () => { this._setStep(3); this._generate(); };
    }

    _initResultSlider() {
      // Tear down any previous slider before re-initialising — otherwise window
      // listeners accumulate every time Step 4 is re-entered.
      this._teardownSlider();

      const wrapper = this.root.querySelector('[data-rv-result]');
      const after = this.root.querySelector('[data-rv-result-after]');
      const slider = this.root.querySelector('[data-rv-result-slider]');
      const handle = this.root.querySelector('.rv-result__handle');
      if (!wrapper || !after || !slider || !handle) return;

      let dragging = false;
      const setPos = (clientX) => {
        const rect = wrapper.getBoundingClientRect();
        const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
        after.style.clipPath = `inset(0 ${(1 - pct) * 100}% 0 0)`;
        slider.style.left = `${pct * 100}%`;
      };
      const initialRect = wrapper.getBoundingClientRect();
      setPos(initialRect.left + initialRect.width * 0.5);

      const onDown = () => { dragging = true; };
      const onMove = (e) => {
        if (!dragging) return;
        const x = e.touches ? e.touches[0].clientX : e.clientX;
        setPos(x);
      };
      const onUp = () => { dragging = false; };

      handle.addEventListener('pointerdown', onDown);
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      this._sliderTeardown = () => {
        handle.removeEventListener('pointerdown', onDown);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        this._sliderTeardown = null;
      };
    }

    _teardownSlider() {
      if (this._sliderTeardown) this._sliderTeardown();
    }

    _populateCtaBlock() {
      const totalEl = this.root.querySelector('[data-rv-total-price]');
      const total = this._calcTotal();
      if (totalEl) totalEl.textContent = total.formatted;

      const potChip = this.root.querySelector('[data-rv-pot-chip]');
      const potVariantId = this._selectedPotVariantId();
      if (potVariantId && potChip) {
        const potCheckedInput = document.querySelector('input[name="addExtraPot"]:checked');
        const potImg = potCheckedInput?.closest('.related-pots')?.querySelector('img');
        const potTitle = potCheckedInput?.closest('.related-pots')?.querySelector('.js-quickview-toggle')?.dataset.title;
        const potImgEl = this.root.querySelector('[data-rv-pot-img]');
        const potLabelEl = this.root.querySelector('[data-rv-pot-label]');
        if (potImgEl && potImg) potImgEl.style.backgroundImage = `url(${potImg.src})`;
        if (potLabelEl && potTitle) potLabelEl.textContent = potTitle;
        potChip.hidden = false;
      } else if (potChip) {
        potChip.hidden = true;
      }

      const addBtn = this.root.querySelector('[data-rv-add-to-cart]');
      if (addBtn) addBtn.onclick = () => this._addToCart();

      const shareBtn = this.root.querySelector('[data-rv-share]');
      if (shareBtn) shareBtn.onclick = () => this._onShare();

      const crossEl = this.root.querySelector('[data-rv-crosssell]');
      if (crossEl) {
        this._renderCrossSellFromOnPagePots(crossEl);
        // Keep the crosssell visible even after a pot is picked so the user
        // can switch to a different pot. We mark the active card via the
        // `is-active` class instead of hiding the whole section.
        crossEl.hidden = false;
        const selectedVariantId = this._selectedPotVariantId();
        crossEl.querySelectorAll('[data-rv-crosssell-add]').forEach(card => {
          const isActive = selectedVariantId && card.dataset.variantId === selectedVariantId;
          card.classList.toggle('is-active', !!isActive);
          card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          card.onclick = () => this._addCrossSell(card);
        });
      }
    }

    // Mirror the on-page pot selector (already rendered with diameter filtering,
    // sorting, and stock checks) instead of duplicating that Liquid logic in the
    // crosssell snippet. Keeps modal + on-page selector in sync from one source.
    _renderCrossSellFromOnPagePots(crossEl) {
      const onPageInputs = Array.from(document.querySelectorAll('input[name="addExtraPot"]'))
        .filter(input => !input.disabled);
      if (!onPageInputs.length) return; // keep server-rendered fallback

      const sorted = onPageInputs
        .map(input => ({ input, priceCents: Number(input.dataset.potPrice || 0) }))
        .sort((a, b) => a.priceCents - b.priceCents)
        .slice(0, 3);
      if (!sorted.length) return;

      const grid = crossEl.querySelector('.rv-crosssell__grid');
      if (!grid) return;
      grid.innerHTML = '';

      for (const { input, priceCents } of sorted) {
        const wrapper = input.closest('.related-pots');
        const imgEl = wrapper?.querySelector('img');
        const title = wrapper?.querySelector('.js-quickview-toggle')?.dataset?.title
          || imgEl?.alt
          || 'Pot';
        const imgSrc = imgEl?.src || '';
        const priceFormatted = `€${(priceCents / 100).toFixed(2).replace('.', ',')}`;

        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'rv-crosssell-card';
        card.setAttribute('data-rv-crosssell-add', '');
        card.dataset.variantId = input.value;
        card.dataset.productId = input.dataset.productId || '';
        card.dataset.potPrice = String(priceCents);
        card.dataset.potTitle = title;
        card.dataset.potImage = imgSrc;

        const imgSpan = document.createElement('span');
        imgSpan.className = 'rv-crosssell-card__img';
        if (imgSrc) imgSpan.style.backgroundImage = `url('${imgSrc}')`;

        const titleSpan = document.createElement('span');
        titleSpan.className = 'rv-crosssell-card__title';
        titleSpan.textContent = title;

        const priceSpan = document.createElement('span');
        priceSpan.className = 'rv-crosssell-card__price';
        priceSpan.textContent = priceFormatted;

        card.append(imgSpan, titleSpan, priceSpan);
        grid.appendChild(card);
      }
    }

    _selectedPotVariantId() {
      const checked = document.querySelector('input[name="addExtraPot"]:checked');
      return checked?.value || null;
    }

    _calcTotal() {
      const productPriceEl = document.querySelector('.js-product-price-display, .js-product-price, [data-product-price], .product__price, .product-price');
      const productPrice = parseFloat(productPriceEl?.dataset?.productPrice || productPriceEl?.textContent?.replace(/[^0-9,]/g, '').replace(',', '.')) || 0;
      const potInput = document.querySelector('input[name="addExtraPot"]:checked');
      const potPrice = parseFloat(potInput?.dataset?.potPrice || 0) / 100;
      const total = productPrice + potPrice;
      return { value: total, formatted: `€${total.toFixed(2).replace('.', ',')}` };
    }

    _setAddToCartState(button, state) {
      if (!button) return;
      const textEl = button.querySelector('.rv-btn__text');
      const iconEl = button.querySelector('.rv-btn__icon i');

      switch (state) {
        case 'loading':
          button.classList.add('is-loading');
          button.classList.remove('is-success');
          button.disabled = true;
          if (textEl) {
            if (!textEl.dataset.originalText) textEl.dataset.originalText = textEl.textContent;
            textEl.textContent = this._t('adding_to_cart');
          }
          if (iconEl) {
            if (!iconEl.dataset.originalClass) iconEl.dataset.originalClass = iconEl.className;
            iconEl.className = 'fa-solid fa-spinner fa-spin';
          }
          break;
        case 'success':
          button.classList.remove('is-loading');
          button.classList.add('is-success');
          if (textEl) textEl.textContent = this._t('added_to_cart');
          if (iconEl) iconEl.className = 'fa-solid fa-check';
          break;
        case 'default':
          button.classList.remove('is-loading', 'is-success');
          button.disabled = false;
          if (textEl && textEl.dataset.originalText) {
            textEl.textContent = textEl.dataset.originalText;
            delete textEl.dataset.originalText;
          }
          if (iconEl && iconEl.dataset.originalClass) {
            iconEl.className = iconEl.dataset.originalClass;
            delete iconEl.dataset.originalClass;
          }
          break;
      }
    }

    async _addToCart() {
      const variantId = this._currentVariantId();
      const potVariantId = this._selectedPotVariantId();
      const plantItem = { id: variantId, quantity: 1 };
      const deliveryMatchProperties = window.deliveryMatchPropertiesFromElement
        ? window.deliveryMatchPropertiesFromElement(this.root)
        : {};
      if (Object.keys(deliveryMatchProperties).length) {
        plantItem.properties = deliveryMatchProperties;
      }
      const items = [plantItem];
      if (potVariantId) items.push({ id: potVariantId, quantity: 1 });

      const addBtn = this.root.querySelector('[data-rv-add-to-cart]');
      this._setAddToCartState(addBtn, 'loading');

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error('Cart add failed');

        this._track('visualizer_addtocart', {
          product_id: this.productId, pot_variant_id: potVariantId, total_value: this._calcTotal().value,
        });

        // Refresh cart UI by replicating what assets/app.js updateCart does.
        // updateCart is defined inside the webpack bundle scope and is not
        // exposed on window, so we cannot call it directly.
        try {
          if (window.setMinicartLoading) window.setMinicartLoading(true);
          const cart = await (await fetch('/cart.js')).json();

          window.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { action: 'add', cart, done: () => {} },
          }));

          const languagePath = window.location.pathname.split('/')[1] || '';
          const minicartUrl = `/${languagePath}/?section_id=base-minicart`;
          const sectionText = await (await fetch(minicartUrl)).text();
          const wrapper = document.createElement('div');
          wrapper.innerHTML = sectionText;
          const newDrawer = wrapper.querySelector('.drawer-cart');
          const currentDrawer = document.querySelector('.drawer-cart');
          if (newDrawer && currentDrawer) {
            currentDrawer.innerHTML = newDrawer.innerHTML;
          }

          document.querySelectorAll('.js-cart-count').forEach(el => {
            el.textContent = cart.item_count;
          });

          // Open the side cart drawer the same way the PDP add-to-cart does.
          // app.js' openMiniCart() toggles `.is-open` on `.drawer-cart`.
          document.querySelector('.drawer-cart')?.classList.add('is-open');
          document.body.classList.add('js-minicart-open');
        } catch (refreshErr) {
          console.warn('[rv] cart refresh failed:', refreshErr);
        } finally {
          if (window.setMinicartLoading) window.setMinicartLoading(false);
        }

        this._setAddToCartState(addBtn, 'success');

        // Brief success state, then close the visualizer modal so the cart
        // drawer is the main focus.
        setTimeout(() => {
          this.close();
          this._setAddToCartState(addBtn, 'default');
        }, 900);
      } catch (err) {
        console.error('[rv] add to cart error:', err);
        alert(this._t('addtocart_failed'));
        this._setAddToCartState(addBtn, 'default');
      }
    }

    _addCrossSell(cardEl) {
      const variantId = cardEl.dataset.variantId;
      const radio = document.querySelector(`input[name="addExtraPot"][value="${variantId}"]`);
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this._track('visualizer_crosssell_add', { pot_variant_id: variantId });
      this._populateCtaBlock();
    }

    async _onShare() {
      const shareUrl = `${this.endpoint}/v/${this.state.imageId}`;
      const shareData = {
        title: this.productTitle,
        text: this._t('share'),
        url: shareUrl,
      };
      if (navigator.share) {
        try {
          await navigator.share(shareData);
          this._track('visualizer_share', { share_method: 'web_share' });
        } catch (e) { /* user cancelled */ }
      } else {
        try {
          await navigator.clipboard.writeText(shareUrl);
          this._toast(this._t('share_copied'));
          this._track('visualizer_share', { share_method: 'copy_link' });
        } catch (e) { console.error('clipboard error', e); }
      }
    }

    _toast(msg) {
      const t = document.createElement('div');
      t.className = 'rv-toast';
      t.textContent = msg;
      document.body.appendChild(t);
      setTimeout(() => t.remove(), 2500);
    }
  }

  function bindRvTriggers(modal) {
    document.querySelectorAll('[data-rv-trigger]').forEach(btn => {
      if (btn.dataset.rvBound) return; // idempotency guard
      btn.dataset.rvBound = '1';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        modal.open();
      });
    });
  }

  function init() {
    const root = document.getElementById('rv-modal');
    if (!root) return;
    const modal = new RoomVisualizerModal(root);
    window.RoomVisualizer = modal;
    bindRvTriggers(modal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind trigger buttons after combined-listing section swap
  window.addEventListener('combined-listing:swapped', function () {
    if (window.RoomVisualizer) {
      bindRvTriggers(window.RoomVisualizer);
    }
  });
})();
